import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "@lib/i18n";
import { func, string, oneOfType, arrayOf } from "prop-types";
import _ from "lodash";
import { useDispatch } from "react-redux";

import TextField from "./TextField";
import MagnifierIcon from "./Icons/MagnifierIcon";
import CloseIcon from "./Icons/Close";
import { lazyLoadGoogleMaps } from "@shared/v2/widgets/googleMapsWidget";

import { mapGeolocationToAddress } from "@lib/mapGeolocationToAddress";
import { addressType } from "@types";

import {
  setTestDrivePointAction,
  setTestDriveSlotAction,
} from "redux/actions/testDriveActions";
import Icon from "./Icons/Icon";

const mapAddressToString = address => {
  if (_.isEmpty(address)) {
    return "";
  }

  if (typeof address === "string") {
    return address;
  }

  if (address.address1) {
    return address.address1;
  }

  const streetNumberPart = address.streetNumber
    ? `${address.streetNumber} `
    : "";
  const streetPart = address.street ? `${address.street}, ` : "";
  const cityPart = address.city ? `${address.city}, ` : "";
  const countryPart = address.country ?? "";

  return `${streetNumberPart}${streetPart}${cityPart}${countryPart}`;
};

const AddressTextField = ({
  initialValue,
  onResetAddress,
  onSetAddress,
  googleAddressTypesRestrictions,
}) => {
  const { t, i18n } = useTranslation();
  const addressInputRef = useRef();
  const dispatch = useDispatch();
  const [address, setAddress] = useState(mapAddressToString(initialValue));
  const autocompleteOptions = {
    ...(googleAddressTypesRestrictions
      ? { types: googleAddressTypesRestrictions }
      : {}),
  };

  useEffect(() => {
    setAddress(mapAddressToString(initialValue));

    if (initialValue) {
      dispatch(setTestDrivePointAction(null));
    }
  }, [initialValue]);

  const handlePlaceSelect = async autocomplete => {
    const {
      geometry,
      address_components: updatedAddress,
      partial_match,
    } = autocomplete.getPlace();
    const hasValidAddress = !partial_match && updatedAddress?.length > 0;

    if (hasValidAddress) {
      const autoCompletedAddress = mapGeolocationToAddress(
        updatedAddress,
        geometry
      );
      await onSetAddress(autoCompletedAddress);
      setAddress(mapAddressToString(autoCompletedAddress));
    }
  };

  const setAddressToNullOnEmptyInput = () => {
    if (!addressInputRef.current.value.length) {
      onResetAddress?.();
    }
  };

  const handleResetOnClick = () => {
    onResetAddress?.();

    setAddress("");

    dispatch(setTestDrivePointAction(null));
    dispatch(setTestDriveSlotAction(null));
  };

  useEffect(() => {
    lazyLoadGoogleMaps(i18n.language).then(maps => {
      const autocomplete = new maps.places.Autocomplete(
        addressInputRef.current,
        autocompleteOptions
      );

      autocomplete.setFields([
        "address_components",
        "formatted_address",
        "geometry",
      ]);

      autocomplete.addListener("place_changed", () =>
        handlePlaceSelect(autocomplete)
      );
    });
  }, []);

  return (
    <TextField
      ref={addressInputRef}
      data-id="address-text-field"
      onBlur={setAddressToNullOnEmptyInput}
      placeholder={t("basket.delivery.address_placeholder")}
      value={address}
      onChange={event => setAddress(event.target.value)}
      suffix={
        <div className="flex items-center">
          <Icon
            className="size-6 text-primaryOrange"
            icon={<MagnifierIcon />}
          />
          {address && (
            <>
              <div className="h-6 w-0.5 bg-greyLight mx-2" />
              <button
                aria-label={t("basket.back")}
                className="grid place-items-center p-0 cursor-pointer"
                data-id={"address-text-field-clear-button"}
                onClick={handleResetOnClick}
              >
                <Icon className="size-4 text-red" icon={<CloseIcon />} />
              </button>
            </>
          )}
        </div>
      }
    />
  );
};

AddressTextField.propTypes = {
  onSetAddress: func.isRequired,
  initialValue: oneOfType([addressType, string]),
  onResetAddress: func,
  customColor: string,
  googleAddressTypesRestrictions: arrayOf(string),
};

export default AddressTextField;
